import type { CSSProperties } from 'react';
import { useState } from 'react';

import { FlameCard } from './FlameCard.js';

interface EmberRevealProps {
  imageUrl: string;
  displayName: string;
}

interface Ember {
  id: number;
  size: number;
  angle: number;
  startDist: number;
  endDist: number;
  delay: number;
  duration: number;
}

function generateEmbers(count: number): Ember[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    size: 4 + Math.random() * 12,
    angle: (360 / count) * i + (Math.random() - 0.5) * 30,
    startDist: 138 + Math.random() * 10,
    endDist: 180 + Math.random() * 60,
    delay: Math.random() * 0.6,
    duration: 1.5 + Math.random() * 1.5,
  }));
}

const embers = generateEmbers(14);

export function EmberReveal({ imageUrl, displayName }: EmberRevealProps) {
  const [animating, setAnimating] = useState(false);

  function handleImageLoad() {
    requestAnimationFrame(() => setAnimating(true));
  }

  return (
    <div className="relative w-[320px] h-[320px] mx-auto flex items-center justify-center">
      {/* Ember particles — only rendered once animation triggers */}
      {animating &&
        embers.map((ember) => {
          const rad = (ember.angle * Math.PI) / 180;
          const dx = Math.cos(rad);
          const dy = Math.sin(rad);

          return (
            <div
              key={ember.id}
              className="absolute rounded-full ember-particle"
              style={
                {
                  width: ember.size,
                  height: ember.size,
                  left: '50%',
                  top: '50%',
                  background:
                    'radial-gradient(circle, rgba(249,114,22,0.9) 0%, rgba(211,46,46,0.4) 100%)',
                  boxShadow: '0 0 4px 1px rgba(255,111,0,0.3)',
                  '--dx': dx,
                  '--dy': dy,
                  '--start-dist': `${ember.startDist}px`,
                  '--end-dist': `${ember.endDist}px`,
                  animationDelay: `${ember.delay}s`,
                  animationDuration: `${ember.duration}s`,
                } as CSSProperties
              }
              aria-hidden="true"
            />
          );
        })}

      {/* Outer glow ring */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          boxShadow: animating
            ? '0 0 40px 8px rgba(249,114,22,0.25), 0 0 80px 20px rgba(211,46,46,0.1)'
            : '0 0 0px 0px rgba(249,114,22,0)',
          transition: 'box-shadow 2s ease-out 0.5s',
        }}
        aria-hidden="true"
      />

      {/* Flame image (circular) */}
      <div
        className="relative z-10"
        style={{
          opacity: animating ? 1 : 0,
          transform: animating ? 'scale(1)' : 'scale(0.85)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        }}
      >
        <FlameCard imageUrl={imageUrl} displayName={displayName} onLoad={handleImageLoad} />
      </div>
    </div>
  );
}
